import { Controller } from '@hotwired/stimulus'
import { camelToSnake } from '../utils/caseConverter'

export default class extends Controller {
  static targets = ['panel', 'badge', 'list', 'empty', 'loadMoreBtn']

  connect() {
    this.isOpen = false
    this.notifications = []
    this.page = 1
    this.hasMore = false
    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    this.handleNewNotification = this.handleNewNotification.bind(this)

    this.fetchUnreadCount()
    this.listenForRealtime()
  }

  disconnect() {
    document.removeEventListener('click', this.closeOnOutsideClick)
    document.removeEventListener('notification_centre:new', this.handleNewNotification)
  }

  // --- Toggle ---

  toggle() {
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.isOpen = true
    this.panelTarget.style.display = ''
    this.page = 1
    this.notifications = []
    this.renderLoading()
    this.fetchNotifications()
    document.addEventListener('click', this.closeOnOutsideClick)
  }

  close() {
    this.isOpen = false
    this.panelTarget.style.display = 'none'
    document.removeEventListener('click', this.closeOnOutsideClick)
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  // --- Real-time ---

  listenForRealtime() {
    document.addEventListener('notification_centre:new', this.handleNewNotification)
  }

  // CableReady camelizes every key of the event detail; the API list is snake_case.
  handleNewNotification(event) {
    const { unread_count, ...notification } = camelToSnake(event.detail)
    this.notifications.unshift(notification)
    this.updateBadge(unread_count)
    if (this.isOpen) this.renderNotifications()
  }

  // --- API calls ---

  async fetchUnreadCount() {
    try {
      const resp = await fetch('/api/v1/notifications/unread_count', { headers: this.headers() })
      if (!resp.ok) return
      const data = await resp.json()
      this.updateBadge(data.unread_count)
    } catch (e) { /* silent */ }
  }

  async fetchNotifications() {
    try {
      const resp = await fetch(`/api/v1/notifications?page=${this.page}`, { headers: this.headers() })
      if (!resp.ok) {
        this.clearSkeletons()
        return
      }
      const data = await resp.json()

      if (this.page === 1) this.notifications = []
      this.notifications.push(...data.notifications)
      this.hasMore = data.pagination.has_next_page

      this.renderNotifications()
    } catch (e) {
      this.clearSkeletons()
    }
  }

  async markRead(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.notificationId
    try {
      await fetch(`/api/v1/notifications/${id}/read`, { method: 'PATCH', headers: this.headers() })
      const notif = this.notifications.find(n => String(n.recipient_id) === String(id))
      if (notif) notif.read = true
      this.fetchUnreadCount()
      this.renderNotifications()
    } catch (e) { /* silent */ }
  }

  async markUnread(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.notificationId
    try {
      await fetch(`/api/v1/notifications/${id}/unread`, { method: 'PATCH', headers: this.headers() })
      const notif = this.notifications.find(n => String(n.recipient_id) === String(id))
      if (notif) notif.read = false
      this.fetchUnreadCount()
      this.renderNotifications()
    } catch (e) { /* silent */ }
  }

  toggleReadMenu(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const id = btn.dataset.notificationId
    const notif = this.notifications.find(n => String(n.recipient_id) === String(id))
    if (!notif) return

    // Remove any existing menu
    this.closeContextMenu()

    const rect = btn.getBoundingClientRect()
    const menu = document.createElement('div')
    menu.className = 'notification-context-menu fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[800]'
    menu.style.top = `${rect.bottom + 4}px`
    menu.style.right = `${window.innerWidth - rect.right}px`

    const label = notif.read ? 'Marcar como não lida' : 'Marcar como lida'
    const action = notif.read ? 'markUnread' : 'markRead'

    menu.innerHTML = `
      <button class="w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 text-left cursor-pointer whitespace-nowrap"
              data-notification-id="${id}" data-menu-action="${action}">
        ${label}
      </button>
    `

    menu.querySelector('button').addEventListener('click', (e) => {
      e.stopPropagation()
      this.closeContextMenu()
      if (action === 'markUnread') this.markUnread(e)
      else this.markRead(e)
    })

    document.body.appendChild(menu)

    // Close on next click anywhere
    setTimeout(() => {
      this._closeMenuHandler = () => this.closeContextMenu()
      document.addEventListener('click', this._closeMenuHandler, { once: true })
    }, 0)
  }

  closeContextMenu() {
    document.querySelectorAll('.notification-context-menu').forEach(el => el.remove())
    if (this._closeMenuHandler) {
      document.removeEventListener('click', this._closeMenuHandler)
      this._closeMenuHandler = null
    }
  }

  async markAllRead() {
    try {
      await fetch('/api/v1/notifications/mark_all_read', { method: 'POST', headers: this.headers() })
      this.notifications.forEach(n => n.read = true)
      this.updateBadge(0)
      this.renderNotifications()
    } catch (e) { /* silent */ }
  }

  loadMore() {
    this.page += 1
    this.fetchNotifications()
  }

  navigateTo(event) {
    const path = event.currentTarget.dataset.resourcePath
    const id = event.currentTarget.dataset.notificationId
    if (id) {
      fetch(`/api/v1/notifications/${id}/read`, {
        method: 'PATCH',
        headers: this.headers(),
        keepalive: true
      }).catch(() => {})
    }
    if (path) window.location.href = path
  }

  // --- Rendering ---

  updateBadge(count) {
    if (count > 0) {
      this.badgeTarget.style.display = ''
      this.badgeTarget.textContent = count > 99 ? '99+' : count
    } else {
      this.badgeTarget.style.display = 'none'
    }
  }

  renderNotifications() {
    this.clearSkeletons()
    this.listTarget.querySelectorAll('.notification-entry').forEach(el => el.remove())

    if (this.notifications.length === 0) {
      this.emptyTarget.style.display = ''
      this.loadMoreBtnTarget.style.display = 'none'
      return
    }

    this.emptyTarget.style.display = 'none'
    this.loadMoreBtnTarget.style.display = this.hasMore ? '' : 'none'

    this.notifications.forEach(n => {
      this.listTarget.insertAdjacentHTML('beforeend', this.notificationHTML(n))
    })
  }

  renderLoading() {
    this.clearSkeletons()
    this.listTarget.querySelectorAll('.notification-entry').forEach(el => el.remove())
    this.emptyTarget.style.display = 'none'
    this.loadMoreBtnTarget.style.display = 'none'

    const skeleton = `
      <div class="notification-skeleton flex items-center gap-2 px-3 py-2 border-b border-gray-100 last:border-b-0 animate-pulse">
        <div class="shrink-0 w-5 h-5 rounded-full bg-gray-200"></div>
        <div class="flex-1 min-w-0 space-y-1.5">
          <div class="h-2.5 bg-gray-200 rounded w-3/4"></div>
          <div class="h-2 bg-gray-100 rounded w-5/6"></div>
          <div class="h-2 bg-gray-100 rounded w-1/4"></div>
        </div>
      </div>
    `
    this.listTarget.insertAdjacentHTML('beforeend', skeleton.repeat(4))
  }

  clearSkeletons() {
    this.listTarget.querySelectorAll('.notification-skeleton').forEach(el => el.remove())
  }

  notificationHTML(n) {
    const bgClass = n.read ? 'bg-white hover:bg-gray-50' : 'bg-yellow-50 hover:bg-yellow-100'
    const timeAgo = this.timeAgo(n.created_at)
    const icon = this.iconSVG(n.icon, n.read)
    const dot = n.read ? '' : '<span class="w-2 h-2 rounded-full bg-yellow-500 shrink-0"></span>'
    const menuAction = n.read ? 'notification-centre#markUnread' : 'notification-centre#markRead'
    const menuLabel = n.read ? 'Marcar como não lida' : 'Marcar como lida'

    return `
      <div class="notification-entry group flex items-center gap-2 px-3 py-2 ${bgClass} cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
           data-notification-id="${this.escapeAttr(String(n.recipient_id))}"
           data-resource-path="${this.escapeAttr(n.resource_path || '')}"
           data-action="click->notification-centre#navigateTo">
        <div class="shrink-0">${icon}</div>
        <div class="flex-1 min-w-0">
          <p class="text-xs font-medium text-gray-900 leading-snug">${this.escapeHtml(n.title)}</p>
          ${n.body ? `<p class="text-xs text-gray-500 leading-relaxed">${this.escapeHtml(n.body)}</p>` : ''}
          <p class="text-[11px] text-gray-400">${timeAgo}</p>
        </div>
        <div class="shrink-0 flex items-center gap-1">
          ${dot}
          <button type="button"
                  class="p-1 rounded-full text-gray-300 hover:text-gray-600 hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-all"
                  data-notification-id="${this.escapeAttr(String(n.recipient_id))}"
                  data-action="click->notification-centre#toggleReadMenu"
                  title="${menuLabel}">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/>
            </svg>
          </button>
        </div>
      </div>
    `
  }

  iconSVG(iconKey, isRead) {
    const color = isRead ? 'text-gray-300' : 'text-yellow-500'
    const icons = {
      'user-plus': `<svg class="w-5 h-5 ${color}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"/></svg>`,
      'message-circle': `<svg class="w-5 h-5 ${color}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"/></svg>`,
      'check-circle': `<svg class="w-5 h-5 ${color}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`,
      'hand-raised': `<svg class="w-5 h-5 ${color}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075-5.925v3m0-3a1.575 1.575 0 013.15 0v3.15M9 11.25v-3.75m0 0a1.575 1.575 0 00-3.15 0v7.5a6.75 6.75 0 006.75 6.75h2.018a5.25 5.25 0 003.712-1.538l1.732-1.732a5.25 5.25 0 001.538-3.712V6.188a1.575 1.575 0 00-3.15 0v3.562"/></svg>`,
      'alert-triangle': `<svg class="w-5 h-5 ${color}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/></svg>`
    }
    return icons[iconKey] || icons['message-circle']
  }

  // --- Utilities ---

  headers() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return { 'Content-Type': 'application/json', 'X-CSRF-Token': token }
  }

  timeAgo(dateStr) {
    const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000)
    if (seconds < 60) return 'Agora'
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
  }

  escapeHtml(str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  escapeAttr(str) {
    if (!str) return ''
    return str.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
}
